/**
 * Local directory config — fields the API does not own yet.
 * Assembled with runtime config in `OrgContextModel`.
 * Onboard: Mongo seed + a `CLIENT_ORG_CONFIGS` entry for remaining local fields.
 */
import {
  InsuranceHandsMono,
  RadiologyScanMri,
  HeartRate,
  Knee,
} from '@florencelabs/icons';
import { mergeWith } from 'lodash';
import { resolveDirectoryDisplayName } from '@provider-directory/functions/resolve-directory-display-name';
import {
  Environment,
  getDeploymentEnvironment,
  getEnvironmentVariables,
} from '@provider-directory/services/getEnvironmentVariables';
import type {
  LocalDirectoryConfig,
  ProviderRedirect,
  AllClientConfigs,
} from '@provider-directory/types';
import type { FeatureFlags } from '@provider-directory/types/flags';
import {
  ContextKeySchema,
  ClientOrgContextKey,
} from '@provider-directory/types/organizations.contracts';
import { type FilterFormGroup } from '@provider-directory/types/search.model';

const MONTEFIORE_PROVIDER_REDIRECTS: ProviderRedirect[] = [
  {
    npi: '1144305509', // Philip O. Ozuah
    url: 'https://montefioreeinstein.org/about/leadership',
  },
  {
    npi: '1982662334', // Yaron Tomer
    url: 'https://einsteinmed.edu/leadership',
  },
];

type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

const mergeConfigs = <T>(configs: (T | DeepPartial<T>)[]): T => {
  return mergeWith({}, ...configs, (objValue: unknown, srcValue: unknown) => {
    if (Array.isArray(objValue)) {
      return srcValue;
    }
  }) as T;
};

const WEIGHT_MANAGEMENT_ERROR_CONTACTS = {
  scenePhoneNumber: '347-498-2445',
  contacts: [
    { name: 'Bronx locations', phone: '866-633-8255', locations: [] },
    { name: 'Elmsford', phone: '914-457-4130', locations: [] },
  ],
};

let _clientOrgConfigs: AllClientConfigs | undefined;

const buildClientOrgConfigs = (): AllClientConfigs => {
  const deployEnvironment = getDeploymentEnvironment();

  return {
    [ContextKeySchema.enum.montefiore_mmc]: {
      name: 'Montefiore Einstein',
      branding: {
        logoUrl: '/montefiore-logo.svg',
        cssVariables: {
          headerColor: '#003769',
        },
        fonts: {
          sans: 'Arial, sans-serif',
          serif: 'LeituraNews, serif',
          file: '/fonts-montefiore.css',
        },
      },
      analytics: {
        gtmId:
          getEnvironmentVariables()
            .NEXT_PUBLIC_GOOGLE_TAG_MANAGER_CONTAINER_ID || undefined,
      },
      cookieBanner: {
        oneTrustDomainScript:
          getEnvironmentVariables().NEXT_PUBLIC_MTF_ONETRUST_DOMAIN_SCRIPT ??
          undefined,
      },
      sitemap: {
        excludeProvidersPath: true,
      },
      /**
       * SEO metadata: Values are i18n translation keys or literal text.
       * Routes MUST implement generateMetadata() and call t() to translate these keys -
       * it will translate if a key is in translation files and
       * if not, it will use the literal value
       * @see apps/provider-directory/src/app/providers/(home)/page.tsx for usage example
       */
      metadata: {
        title: 'metadata.pageTitle',
        description: 'metadata.searchDescription',
        keywords: 'metadata.pageKeywords',
        openGraph: {
          title: 'metadata.pageTitle',
          description: 'metadata.searchDescription',
        },
        metadataBase: new URL('https://doctors.montefioreeinstein.org'),
        icons: {
          icon: [
            { url: '/favicons/mmc/favicon.ico' },
            { url: '/favicons/mmc/favicon-16x16.png', sizes: '16x16' },
            { url: '/favicons/mmc/favicon-32x32.png', sizes: '32x32' },
            { url: '/favicons/mmc/favicon-128x128.png', sizes: '128x128' },
          ],
          apple: [
            {
              url: '/favicons/mmc/apple-touch-icon-180x180.png',
              sizes: '180x180',
              type: 'image/png',
            },
          ],
        },
      },
      homepageContent: {
        allSpecialties: {
          enabled: true,
          content: [
            {
              name: 'specialties.allergyImmunology',
              slug: 'allergy-and-immunology',
            },
            {
              name: 'specialties.oncology',
              href: 'https://cancer.montefioreeinstein.org/doctors/profiles',
            },
            {
              name: 'specialties.cardiothoracicSurgery',
              slug: 'cardiothoracic-surgery',
            },
            {
              name: 'specialties.dentistry',
              slug: 'dentistry',
            },
            {
              name: 'specialties.dermatology',
              slug: 'dermatology',
            },
            {
              name: 'specialties.endocrinology',
              slug: 'endocrinology,-diabetes,-metabolism',
            },
            {
              name: 'specialties.familyPractice',
              slug: 'family-practice',
            },
            {
              name: 'specialties.gastroenterology',
              slug: 'gastroenterology',
            },
            {
              name: 'specialties.hematology',
              slug: 'hematology',
            },
            {
              name: 'specialties.hepatology',
              slug: 'hepatology',
            },
            {
              name: 'specialties.palliative',
              slug: 'hospice-and-palliative-medicine',
            },
            {
              name: 'specialties.infectiousDisease',
              slug: 'infectious-disease',
            },
            {
              name: 'specialties.nephrology',
              slug: 'nephrology',
            },
            {
              name: 'specialties.neurologicalSurgery',
              slug: 'neurological-surgery',
            },
            {
              name: 'specialties.neurology',
              slug: 'neurology',
            },
            {
              name: 'specialties.obstetricsGynecology',
              slug: 'obstetrics-and-gynecology',
            },
            {
              name: 'specialties.ophthalmology',
              slug: 'ophthalmology',
            },
            {
              name: 'specialties.orthopedicSurgery',
              slug: 'orthopedic-surgery',
            },
            {
              name: 'specialties.ent',
              slug: 'otolaryngology',
            },
            {
              name: 'specialties.pediatrics',
              slug: 'pediatrics',
            },
            {
              name: 'specialties.transplantSurgery',
              slug: 'transplant-surgery',
            },
          ],
        },
        cta: {
          enabled: false,
        },
        frequentlySearchedSpecialties: {
          enabled: true,
          content: [
            {
              icon: InsuranceHandsMono,
              title: 'frequently_searched_specialties.primary_care_card.title',
              body: 'frequently_searched_specialties.primary_care_card.body',
              slug: 'primary-care',
            },
            {
              icon: RadiologyScanMri,
              title: 'frequently_searched_specialties.imaging_card.title',
              body: 'frequently_searched_specialties.imaging_card.body',
              slug: 'radiology',
            },
          ],
        },
      },
      decisionTree: {
        decisionTreeAnswersUsage: 'skip',
        mainPhoneNumber: '800-MD-MONTE',
        contacts: [
          {
            phone: '718-920-5949',
            description:
              'for our Bronx locations at 3400 Bainbridge Ave and 1250 Waters Place',
            locations: [],
          },
          {
            phone: '718-892-7817',
            description: 'for our Bronx location at 1578 Williamsbridge Road',
            locations: [],
          },
          {
            phone: '718-884-3900',
            description:
              'for our Bronx location at 2711 Henry Hudson Pkwy West',
            locations: [],
          },
          {
            phone: '718-933-2244',
            description:
              'for our Bronx location at 3201 Grand Concourse and our Yonkers location at 1010 Central Park Ave',
            locations: [],
          },
          {
            phone: '914-633-7870',
            description: 'for our New Rochelle location at 20 Cedar Street',
            locations: [],
          },
          {
            phone: '914-235-3535',
            description: 'for our Mamaroneck location at 933 Mamaroneck Ave',
            locations: [],
          },
          {
            phone: '212-324-4222',
            description: 'for our Manhattan location at 385 9th Ave',
            locations: [],
          },
          {
            phone: '914-457-4130',
            description: 'for our Elmsford location at 555 Taxter Road',
            locations: [],
          },
        ],
        scenesConfig: {
          'is-adult-cardiology-error': {
            scenePhoneNumber: '718-741-2343',
          },
          'is-adult-dermatology-error': {
            scenePhoneNumber: '866-633-8255',
            contacts: [
              {
                name: 'Bronx and Scarsdale locations',
                phone: '866-633-8255',
                locations: [],
              },
              {
                name: 'Manhattan locations',
                phone: '212-324-4222',
                locations: [],
              },
              { name: 'Elmsford', phone: '914-457-4130', locations: [] },
            ],
          },
          'is-adult-allergy-error': {
            scenePhoneNumber: '866-633-8255',
            contacts: [
              {
                name: 'Bronx and Scarsdale locations',
                phone: '866-633-8255',
                locations: [],
              },
              { name: 'Elmsford', phone: '914-457-4130', locations: [] },
            ],
          },
          'seen-dermatology-recently-error': {
            scenePhoneNumber: '866-633-8255',
            contacts: [
              {
                name: 'Bronx and Scarsdale locations',
                phone: '866-633-8255',
                locations: [],
              },
              {
                name: 'Manhattan locations',
                phone: '212-324-4222',
                locations: [],
              },
              { name: 'Elmsford', phone: '914-457-4130', locations: [] },
            ],
          },
          'seen-allergy-recently-error': {
            scenePhoneNumber: '866-633-8255',
            contacts: [
              {
                name: 'Bronx and Scarsdale locations',
                phone: '866-633-8255',
                locations: [],
              },
              { name: 'Elmsford', phone: '914-457-4130', locations: [] },
            ],
          },
          'is-adult-ortho-error': {
            scenePhoneNumber: '718-920-2060',
            contacts: [
              {
                name: 'Bronx, Mount Vernon, and New Rochelle locations',
                phone: '718-920-2060',
                locations: [],
              },
              {
                name: 'West Nyack, Airmont, Monroe, and Dobbs Ferry locations',
                phone: '845-358-1000',
                locations: [],
              },
              { name: 'New Windsor', phone: '845-580-8208', locations: [] },
              { name: 'Manhattan', phone: '212-324-4222', locations: [] },
              { name: 'Elmsford', phone: '914-457-4130', locations: [] },
            ],
          },
          'seen-ortho-recently-error': {
            scenePhoneNumber: '718-920-2060',
            contacts: [
              {
                name: 'Bronx, Mount Vernon, and New Rochelle locations',
                phone: '718-920-2060',
                locations: [],
              },
              {
                name: 'West Nyack, Airmont, Monroe, and Dobbs Ferry locations',
                phone: '845-358-1000',
                locations: [],
              },
              { name: 'New Windsor', phone: '845-580-8208', locations: [] },
              {
                name: 'Manhattan locations',
                phone: '212-324-4222',
                locations: [],
              },
              { name: 'Elmsford', phone: '914-457-4130', locations: [] },
            ],
          },
          'ortho-workers-comp-error': {
            scenePhoneNumber: '718-920-2060',
            contacts: [
              {
                name: 'Bronx, Mount Vernon, and New Rochelle locations',
                phone: '718-920-2060',
                locations: [],
              },
              {
                name: 'West Nyack, Airmont, Monroe, and Dobbs Ferry locations',
                phone: '845-358-1000',
                locations: [],
              },
              { name: 'New Windsor', phone: '845-580-8208', locations: [] },
              { name: 'Manhattan', phone: '212-324-4222', locations: [] },
              { name: 'Elmsford', phone: '914-457-4130', locations: [] },
            ],
          },
          'foot-care-podiatry-error': {
            scenePhoneNumber: '347-341-4300',
          },
          'is-adult-endocrinology-error': {
            scenePhoneNumber: '718-741-2450',
          },
          'seen-endocrinology-recently-error': {
            scenePhoneNumber: '866-633-8255',
            contacts: [
              { name: 'Bronx locations', phone: '866-633-8255', locations: [] },
              { name: 'Elmsford', phone: '914-457-4130', locations: [] },
            ],
          },
          'endocrinology-pregnancy-error': {
            scenePhoneNumber: '866-633-8255',
            contacts: [
              { name: 'Bronx locations', phone: '347-498-2445', locations: [] },
              { name: 'Elmsford', phone: '914-457-4130', locations: [] },
            ],
          },
          'endocrinology-fertility-error': {
            scenePhoneNumber: '914-997-1060',
          },
          'endocrinology-weight-management-check-error':
            WEIGHT_MANAGEMENT_ERROR_CONTACTS,
          'is-adult-gastroenterology-error': {
            scenePhoneNumber: '718-741-2450',
          },
          'seen-gastroenterology-recently-error': {
            contacts: [
              {
                phone: '866-633-8255',
                description: 'for the following locations:',
                locations: [
                  { location: '3400 Bainbridge Ave, Bronx, NY' },
                  { location: '1250 Waters Place - Tower II, Bronx, NY' },
                ],
              },
              {
                phone: '718-239-0115',
                description:
                  'for our 1250 Waters Place - Tower I, Bronx, NY location',
                locations: [],
              },
              {
                phone: '914-725-9115',
                description:
                  'for our 688 White Plains Rd, Scarsdale, NY location',
                locations: [],
              },
              {
                phone: '914-561-4360',
                description: 'for our 18 Ashford Ave, Dobbs Ferry, NY location',
                locations: [],
              },
              {
                phone: '914-375-0022',
                description:
                  'for our 1010 Central Park Ave, Yonkers, NY location',
                locations: [],
              },
              {
                phone: '914-457-4130',
                description: 'for our 555 Taxter Rd, Elmsford, NY location',
                locations: [],
              },
              {
                phone: '212-324-4222',
                description: 'for the following locations:',
                locations: [
                  { location: '385 9th Avenue, New York, NY' },
                  { location: '2633 Broadway, New York, NY' },
                ],
              },
            ],
          },
          'gastroenterology-urgent-error': {
            contacts: [
              {
                phone: '866-633-8255',
                description: 'for the following locations:',
                locations: [
                  { location: '3400 Bainbridge Ave, Bronx, NY' },
                  { location: '1250 Waters Place - Tower II, Bronx, NY' },
                ],
              },
              {
                phone: '718-239-0115',
                description:
                  'for our 1250 Waters Place - Tower I, Bronx, NY location',
                locations: [],
              },
              {
                phone: '914-725-9115',
                description:
                  'for our 688 White Plains Rd, Scarsdale, NY location',
                locations: [],
              },
              {
                phone: '914-561-4360',
                description: 'for our 18 Ashford Ave, Dobbs Ferry, NY location',
                locations: [],
              },
              {
                phone: '914-375-0022',
                description:
                  'for our 1010 Central Park Ave, Yonkers, NY location',
                locations: [],
              },
              {
                phone: '914-457-4130',
                description: 'for our 555 Taxter Rd, Elmsford, NY location',
                locations: [],
              },
              {
                phone: '212-324-4222',
                description: 'for the following locations:',
                locations: [
                  { location: '385 9th Avenue, New York, NY' },
                  { location: '2633 Broadway, New York, NY' },
                ],
              },
            ],
          },
          'pediatrics-newborn-error': {
            contacts: [
              {
                name: 'Montefiore Medical Group',
                phone: '800-MD-MONTE',
                locations: [],
              },
              {
                name: 'Montefiore Einstein Advanced Care in Manhattan',
                phone: '212-324-4222',
                locations: [],
              },
              {
                name: 'Montefiore Einstein Advanced Care in Elmsford',
                phone: '914-457-4130',
                locations: [],
              },
            ],
          },
          'pediatrics-established-pediatric-error': {
            contacts: [
              {
                name: 'Montefiore Medical Group',
                phone: '800-MD-MONTE',
                locations: [],
              },
              {
                name: 'Montefiore Einstein Advanced Care in Manhattan',
                phone: '212-324-4222',
                locations: [],
              },
              {
                name: 'Montefiore Einstein Advanced Care in Elmsford',
                phone: '914-457-4130',
                locations: [],
              },
            ],
          },
          'pediatrics-established-adult-error': {
            contacts: [
              {
                name: 'Montefiore Medical Group',
                phone: '800-MD-MONTE',
                locations: [],
              },
              {
                name: 'Montefiore Einstein Advanced Care in Manhattan',
                phone: '212-324-4222',
                locations: [],
              },
              {
                name: 'Montefiore Einstein Advanced Care in Elmsford',
                phone: '914-457-4130',
                locations: [],
              },
            ],
          },
        },
        specialtyMap: {
          // Cardiology
          'advanced heart failure and transplant cardiology': 'cardiology',
          arrhythmia: 'cardiology',
          'cardiac electrophysiology': 'cardiology',
          'cardio-genetics': 'cardiology',
          'cardio-obstetrics': 'cardiology',
          'cardio-oncology': 'cardiology',
          'cardiothoracic surgery': 'cardiology',
          'cardiovascular disease': 'cardiology',
          'general cardiology': 'cardiology',
          'hypertrophic cardiomyopathy': 'cardiology',
          'interventional cardiology': 'cardiology',
          'preventive cardiology': 'cardiology',
          'structural-valvular heart disease': 'cardiology',
          // Dermatology
          dermatology: 'dermatology',
          'general dermatology': 'dermatology',
          'cosmetic dermatology': 'dermatology',
          'pediatric dermatology': 'dermatology',
          'dermatologic surgery': 'dermatology',
          'mohs surgery': 'dermatology',
          // Allergy
          'food allergies': 'allergy',
          'drug allergies': 'allergy',
          'general allergy and immunology': 'allergy',
          'allergy and immunology': 'allergy',
          // Endocrinology
          'pediatric endocrinology and diabetes': 'endocrinology',
          'endocrinology, diabetes and metabolism': 'endocrinology',
          'reproductive endocrinology and infertility': 'endocrinology',
          'type 1 diabetes': 'endocrinology',
          'type 2 diabetes': 'endocrinology',
          'thyroid cancer': 'endocrinology',
          'thyroid disorders': 'endocrinology',
          'osteoporosis and metabolic bone disease': 'endocrinology',
          'weight management': 'endocrinology',
          'pituitary and adrenal disorders': 'endocrinology',
          // Gastroenterology
          gastroenterology: 'gastroenterology',
          'pediatric gastroenterology': 'gastroenterology',
          // Orthopedics
          'orthopedic surgery': 'orthopedics',
          'hand surgery': 'orthopedics',
          'wrist surgery': 'orthopedics',
          'foot and ankle surgery': 'orthopedics',
          'knee surgery': 'orthopedics',
          'hip surgery': 'orthopedics',
          'shoulder surgery': 'orthopedics',
          'spine surgery': 'orthopedics',
          'orthopedic trauma': 'orthopedics',
          'orthopedic oncology': 'orthopedics',
          'joint replacement': 'orthopedics',
          'sports medicine': 'orthopedics',
          podiatry: 'orthopedics',
          'upper extremity surgery': 'orthopedics',
          scoliosis: 'orthopedics',
          pediatrics: 'pediatrics',
        },
      },
      iconIsoRedirect: '/favicons/mmc/favicon.ico',
      appleTouchIconPath: '/favicons/mmc/apple-touch-icon-180x180.png',
      appointmentTypes: [
        {
          key: 'in-person',
          visitTypeId: '1437',
          label: 'visitType.newPatientConsult.label',
          description: 'visitType.newPatientConsult.description',
          modality: 'in-person',
        },
        {
          key: 'annual-physical',
          visitTypeId: '1201',
          label: 'visitType.annualPhysical.label',
          description: 'visitType.annualPhysical.description',
          modality: 'in-person',
        },
      ],
      defaultMaxDistance: 5,
      legal: {
        smsTermsUrl:
          'https://mychart.montefiore.org/mychart/Authentication/Login?mode=stdfile&option=termsandconditions',
        privacyPolicyUrl:
          'https://montefioreeinstein.org/patient-care/privacy-policy/web-mobile',
      },
      findDoctorUrl: {
        [Environment.PROD]: 'https://montefioreeinstein.org/profiles',
        [Environment.STAGING]: undefined,
        [Environment.QA]: undefined,
        [Environment.DEV]: undefined,
        [Environment.LOCAL]: undefined,
      }[deployEnvironment],
      hasOwnFindDoctorPage: true,
      providerRedirects: MONTEFIORE_PROVIDER_REDIRECTS,
      translation: {
        enabledFields: [
          'biography',
          'gender',
          'languages',
          'degrees',
          'specialties',
          'clinicalFocus',
          'researchFocus',
          'fellowships',
          'medicalEducation',
          'residencies',
          'internship',
          'conditions',
          'boardCertifications',
          'locationNames',
        ],
        glossaryId: 'montefiore-glossary',
      },
      sorting: {
        options: [
          {
            type: 'item',
            label: 'searchFilters.availability',
            value: 'availability',
            query: '-scheduling,-availability,-networks',
            queryWithDistance: '-scheduling,-relevance,-networks',
          },
          {
            type: 'item',
            label: 'searchFilters.distance',
            value: 'distance',
            query: 'distance',
          },
          {
            type: 'section',
            section: 'searchFilters.alphabetical',
            label: 'A – Z',
            value: 'name',
            query: 'name',
          },
          {
            type: 'section',
            section: 'searchFilters.alphabetical',
            label: 'Z – A',
            value: '-name',
            query: '-name',
          },
        ],
        default: 'availability',
      },
      sdk: {
        directoryUrl: {
          [Environment.PROD]: 'https://doctors.montefioreeinstein.org',
          [Environment.QA]: 'https://mtf.pds.qa.fabrichealth.com',
          [Environment.STAGING]: 'https://mtf.pds.stage.fabrichealth.com',
          [Environment.DEV]: 'https://mtf.pds.dev.fabrichealth.com',
          [Environment.LOCAL]: 'http://localhost:3000',
        }[deployEnvironment],
      },
    },
    [ContextKeySchema.enum.montefiore_wph]: {
      name: 'White Plains Hospital',
      branding: {
        logoUrl: '/wphospital-logo.svg',
        cssVariables: {
          headerColor: '#003763',
        },
        fonts: {
          sans: { key: 'lato', source: 'GOOGLE' },
          serif: { key: 'lato', source: 'GOOGLE' },
        },
      },
      analytics: {
        gtmId:
          getEnvironmentVariables()
            .NEXT_PUBLIC_GOOGLE_TAG_MANAGER_CONTAINER_ID_WPH || undefined,
      },
      sitemap: {},
      metadata: {
        title: 'metadata.pageTitle',
        description: 'metadata.searchDescription',
        keywords: 'metadata.pageKeywords',
        openGraph: {
          title: 'metadata.pageTitle',
          description: 'metadata.searchDescription',
        },
        metadataBase: new URL('https://doctors.wphospital.org'),
        icons: {
          icon: [
            { url: '/favicons/wph/favicon.ico' },
            { url: '/favicons/wph/favicon-16x16.png', sizes: '16x16' },
            { url: '/favicons/wph/favicon-32x32.png', sizes: '32x32' },
          ],
          apple: [
            {
              url: '/favicons/wph/apple-touch-icon.png',
              sizes: '180x180',
              type: 'image/png',
            },
          ],
        },
      },
      homepageContent: {
        allSpecialties: {
          enabled: true,
          content: [
            {
              name: 'specialties.allergyImmunology',
              slug: 'allergy-and-immunology',
            },
            {
              name: 'specialties.hematologyOncology',
              href: '/providers/find?provider-group=cancer-specialty-group',
            },
            {
              name: 'specialties.dermatology',
              slug: 'dermatology',
            },
            {
              name: 'specialties.endocrinology',
              href: '/providers/find?provider-group=endocrinology-specialty-group',
            },
            {
              name: 'specialties.otolaryngology',
              href: '/providers/find?provider-group=otolaryngology-specialty-group',
            },
            {
              name: 'specialties.gastroenterology',
              href: '/providers/find?provider-group=gastroenterology-specialty-group',
            },
            {
              name: 'specialties.hepatology',
              slug: 'hepatology',
            },
            {
              name: 'specialties.palliative',
              slug: 'hospice-and-palliative-medicine',
            },
            {
              name: 'specialties.infectiousDisease',
              href: '/providers/find?provider-group=infectious-disease-specialty-group',
            },
            {
              name: 'specialties.nephrology',
              href: '/providers/find?provider-group=nephrology-specialty-group',
            },
            {
              name: 'specialties.neurosciences',
              href: '/providers/find?provider-group=neuro-specialty-group',
            },
            {
              name: 'specialties.obstetricsGynecology',
              href: '/providers/find?provider-group=obgyn-specialty-group',
            },
            {
              name: 'specialties.pediatrics',
              slug: 'pediatrics',
            },
            {
              name: 'specialties.plasticSurgery',
              href: '/providers/find?provider-group=plastic-surgery-specialty-group',
            },
            {
              name: 'specialties.primaryCare',
              slug: 'primary-care',
            },
            {
              name: 'specialties.pulmonology',
              href: '/providers/find?provider-group=pulmonology-and-sleep-specialty-group',
            },
            {
              name: 'specialties.woundCare',
              slug: 'wound-care',
            },
          ],
        },
        cta: {
          enabled: true,
          settings: {
            title: 'cta.title',
            body: 'cta.body',
            phone: '914-849-MYMD',
            image: {
              src: '/wph-find-doctor.webp',
              alt: 'Call to find doctor',
            },
          },
        },
        frequentlySearchedSpecialties: {
          enabled: true,
          content: [
            {
              icon: HeartRate,
              title:
                'frequently_searched_specialties.cardiac_services_card.title',
              body: 'frequently_searched_specialties.cardiac_services_card.body',
              href: '/providers/find?provider-group=cardiology-specialty-group',
            },
            {
              icon: Knee,
              title:
                'frequently_searched_specialties.orthopedic_services_card.title',
              body: 'frequently_searched_specialties.orthopedic_services_card.body',
              slug: 'orthopedic-surgery',
            },
          ],
        },
      },
      decisionTree: {
        decisionTreeAnswersUsage: 'skip',
        mainPhoneNumber: '914-WPH-FIND',
        contacts: [
          {
            phone: '914-723-7800',
            locations: [
              { location: 'Scarsdale Medical Group (550 Mamaroneck)' },
            ],
          },
          {
            phone: '914-242-2020',
            locations: [
              {
                location:
                  'Westchester Dermatology and Mohs Surgery (Mount Kisco)',
              },
            ],
          },
          {
            phone: '914-472-7023',
            locations: [
              {
                location:
                  'Westchester Dermatology and Mohs Surgery (Scarsdale)',
              },
            ],
          },
          {
            phone: '914-637-2663',
            locations: [{ location: 'WPHPA of New Rochelle' }],
          },
        ],
        scenesConfig: {
          'is-adult-cardiology-error': {
            scenePhoneNumber: '914-849-5437',
          },
          'is-adult-allergy-error': {
            scenePhoneNumber: '914-WPH-ALRG',
          },
          'seen-allergy-recently-error': {
            scenePhoneNumber: '914-WPH-ALRG',
          },
          'is-adult-dermatology-error': {
            contacts: [
              {
                phone: '914-723-7800',
                locations: [
                  { location: 'Scarsdale Medical Group (550 Mamaroneck)' },
                ],
              },
              {
                phone: '914-242-2020',
                locations: [
                  {
                    location:
                      'Westchester Dermatology and Mohs Surgery (Mount Kisco)',
                  },
                ],
              },
              {
                phone: '914-472-7023',
                locations: [
                  {
                    location:
                      'Westchester Dermatology and Mohs Surgery (Scarsdale)',
                  },
                ],
              },
              {
                phone: '914-637-2663',
                locations: [{ location: 'WPHPA of New Rochelle' }],
              },
            ],
          },
          'seen-cardiology-recently-error': {
            scenePhoneNumber: '914-WPH-BEAT',
          },
          'seen-dermatology-recently-error': {
            contacts: [
              {
                phone: '914-723-7800',
                locations: [
                  { location: 'Scarsdale Medical Group (550 Mamaroneck)' },
                ],
              },
              {
                phone: '914-242-2020',
                locations: [
                  {
                    location:
                      'Westchester Dermatology and Mohs Surgery (Mount Kisco)',
                  },
                ],
              },
              {
                phone: '914-472-7023',
                locations: [
                  {
                    location:
                      'Westchester Dermatology and Mohs Surgery (Scarsdale)',
                  },
                ],
              },
              {
                phone: '914-637-2663',
                locations: [{ location: 'WPHPA of New Rochelle' }],
              },
            ],
          },
          'reason-for-visit-error': {
            scenePhoneNumber: '914-WPH-BEAT',
          },
          'is-adult-ortho-error': {
            scenePhoneNumber: '914-849-7897',
          },
          'seen-ortho-recently-error': {
            scenePhoneNumber: '914-849-7897',
          },
          'ortho-workers-comp-error': {
            scenePhoneNumber: '914-849-7897',
          },
          'foot-care-podiatry-error': {
            scenePhoneNumber: '347-341-4300',
          },
          'is-adult-endocrinology-error': {
            scenePhoneNumber: '914-849-KIDS',
          },
          'seen-endocrinology-recently-error': {
            scenePhoneNumber: '914-WPH-ENDO',
          },
          'endocrinology-pregnancy-error': {
            scenePhoneNumber: '917-WPH-ENDO',
          },
          'endocrinology-fertility-error': {
            scenePhoneNumber: '914-997-1060',
          },
          'endocrinology-weight-management-check-error': {
            scenePhoneNumber: '914-974-3463',
          },
          'is-adult-gastroenterology-error': {
            scenePhoneNumber: '914-849-KIDS',
          },
          'seen-gastroenterology-recently-error': {
            scenePhoneNumber: '914-WP-GSTRO',
          },
          'gastroenterology-urgent-error': {
            scenePhoneNumber: '914-WP-GSTRO',
          },
          'pediatrics-newborn-error': {
            contacts: [
              {
                phone: '914-849-7900',
                description: 'for WPHPA of Scarsdale',
                locations: [],
              },
              {
                phone: '914-989-1111',
                description: 'for Scarsdale Medical Group',
                locations: [],
              },
              {
                phone: '914-849-7900',
                description:
                  'for White Plains Hospital Medical & Wellness (Armonk)',
                locations: [],
              },
              {
                phone: '914-849-7075',
                description: 'for WPHPA of Somers',
                locations: [],
              },
            ],
          },
          'pediatrics-established-pediatric-error': {
            contacts: [
              {
                phone: '914-849-7900',
                description: 'for WPHPA of Scarsdale',
                locations: [],
              },
              {
                phone: '914-989-1111',
                description: 'for Scarsdale Medical Group',
                locations: [],
              },
              {
                phone: '914-849-7900',
                description:
                  'for White Plains Hospital Medical & Wellness (Armonk)',
                locations: [],
              },
              {
                phone: '914-849-7075',
                description: 'for WPHPA of Somers',
                locations: [],
              },
            ],
          },
          'pediatrics-established-adult-error': {
            scenePhoneNumber: '914-WPH-FIND',
          },
        },
        specialtyMap: {
          // cardiology
          'advanced heart failure and transplant cardiology': 'cardiology',
          arrhythmia: 'cardiology',
          'cardiac electrophysiology': 'cardiology',
          'cardio-genetics': 'cardiology',
          'cardio-obstetrics': 'cardiology',
          'cardio-oncology': 'cardiology',
          'cardiothoracic surgery': 'cardiology',
          'cardiovascular disease': 'cardiology',
          'general cardiology': 'cardiology',
          'hypertrophic cardiomyopathy': 'cardiology',
          'interventional cardiology': 'cardiology',
          'preventive cardiology': 'cardiology',
          'structural-valvular heart disease': 'cardiology',
          // dermatology
          dermatology: 'dermatology',
          'general dermatology': 'dermatology',
          'cosmetic dermatology': 'dermatology',
          'pediatric dermatology': 'dermatology',
          'dermatologic surgery': 'dermatology',
          'mohs surgery': 'dermatology',
          // Allergy
          'food allergies': 'allergy',
          'drug allergies': 'allergy',
          'general allergy and immunology': 'allergy',
          'allergy and immunology': 'allergy',
          // Endocrinology
          'pediatric endocrinology and diabetes': 'endocrinology',
          'endocrinology, diabetes and metabolism': 'endocrinology',
          'reproductive endocrinology and infertility': 'endocrinology',
          'type 1 diabetes': 'endocrinology',
          'type 2 diabetes': 'endocrinology',
          'thyroid cancer': 'endocrinology',
          'thyroid disorders': 'endocrinology',
          'osteoporosis and metabolic bone disease': 'endocrinology',
          'weight management': 'endocrinology',
          'pituitary and adrenal disorders': 'endocrinology',
          // gastroenterology
          gastroenterology: 'gastroenterology',
          'pediatric gastroenterology': 'gastroenterology',
          'transplant hepatology': 'gastroenterology',
          // orthopedics
          'orthopedic surgery': 'orthopedics',
          'hand surgery': 'orthopedics',
          'wrist surgery': 'orthopedics',
          'foot and ankle surgery': 'orthopedics',
          'knee surgery': 'orthopedics',
          'hip surgery': 'orthopedics',
          'shoulder surgery': 'orthopedics',
          'spine surgery': 'orthopedics',
          'orthopedic trauma': 'orthopedics',
          'orthopedic oncology': 'orthopedics',
          'joint replacement': 'orthopedics',
          'sports medicine': 'orthopedics',
          podiatry: 'orthopedics',
          'upper extremity surgery': 'orthopedics',
          scoliosis: 'orthopedics',
          pediatrics: 'pediatrics',
        },
      },
      iconIsoRedirect: '/favicons/wph/favicon.ico',
      appleTouchIconPath: '/favicons/wph/apple-touch-icon.png',
      appointmentTypes: [
        {
          key: 'in-person',
          visitTypeId: '1437',
          label: 'visitType.newPatientConsult.label',
          description: 'visitType.newPatientConsult.description',
          modality: 'in-person',
        },
        {
          key: 'skin-check',
          visitTypeId: '325',
          label: 'visitType.generalTelehealth.label',
          description: 'visitType.generalTelehealth.description',
          modality: 'in-person',
        },
        {
          key: 'cosmetics',
          visitTypeId: '500',
          label: 'visitType.generalTelehealth.label',
          description: 'visitType.generalTelehealth.description',
          modality: 'in-person',
        },
        {
          key: 'annual-physical',
          visitTypeId: '1201',
          label: 'visitType.annualPhysical.label',
          description: 'visitType.annualPhysical.description',
          modality: 'in-person',
        },
      ],
      defaultMaxDistance: 10,
      legal: {
        smsTermsUrl:
          'https://mychart.montefiore.org/mychart/Authentication/Login?mode=stdfile&option=termsandconditions',
        privacyPolicyUrl:
          'https://montefioreeinstein.org/patient-care/privacy-policy/web-mobile',
      },
      providerRedirects: MONTEFIORE_PROVIDER_REDIRECTS,
      translation: {
        enabledFields: [
          'biography',
          'gender',
          'languages',
          'degrees',
          'specialties',
          'clinicalFocus',
          'researchFocus',
          'fellowships',
          'medicalEducation',
          'residencies',
          'internship',
          'conditions',
          'boardCertifications',
          'locationNames',
        ],
        glossaryId: 'montefiore-glossary',
      },
      sorting: {
        options: [
          {
            type: 'item',
            label: 'searchFilters.availability',
            value: 'availability',
            query: '-scheduling,-availability,-networks',
            queryWithDistance: '-scheduling,-relevance,-networks',
          },
          {
            type: 'item',
            label: 'searchFilters.distance',
            value: 'distance',
            query: 'distance',
          },
          {
            type: 'section',
            section: 'searchFilters.alphabetical',
            label: 'A – Z',
            value: 'name',
            query: 'name',
          },
          {
            type: 'section',
            section: 'searchFilters.alphabetical',
            label: 'Z – A',
            value: '-name',
            query: '-name',
          },
        ],
        default: 'availability',
      },
      sdk: {
        directoryUrl: {
          [Environment.PROD]: 'https://doctors.wphospital.org',
          [Environment.QA]: 'https://wph.pds.qa.fabrichealth.com',
          [Environment.STAGING]: 'https://wph.pds.stage.fabrichealth.com',
          [Environment.DEV]: 'https://wph.pds.dev.fabrichealth.com',
          [Environment.LOCAL]: 'http://localhost:3000',
        }[deployEnvironment],
      },
    },
    [ContextKeySchema.enum.montefiore_meac]: {
      name: 'Montefiore Einstein Advanced Care',
      // D11: the legacy `parentOrgFindDoctorUrl` sent MEAC to its OWN doctors host
      // while the banner copy said "Montefiore Einstein". Link and copy now agree.
      branding: {
        logoUrl:
          'https://assets.montefioreeinstein.org/meac/logo/meac_centered_logo_rgb.svg',
        cssVariables: {
          headerColor: '#003769',
        },
        fonts: {
          sans: 'Arial, sans-serif',
          serif: 'LeituraNews, serif',
          file: '/fonts-montefiore.css',
        },
      },
      analytics: {
        gtmId:
          getEnvironmentVariables()
            .NEXT_PUBLIC_GOOGLE_TAG_MANAGER_CONTAINER_ID_MEAC || undefined,
      },
      cookieBanner: {
        oneTrustDomainScript:
          getEnvironmentVariables().NEXT_PUBLIC_MEAC_ONETRUST_DOMAIN_SCRIPT ??
          undefined,
      },
      sitemap: {
        excludeProvidersPath: true,
      },
      metadata: {
        title: 'metadata.pageTitle',
        description: 'metadata.searchDescription',
        keywords: 'metadata.pageKeywords',
        openGraph: {
          title: 'metadata.pageTitle',
          description: 'metadata.searchDescription',
        },
        metadataBase: new URL(
          'https://doctors.montefioreeinsteinadvancedcare.org',
        ),
        icons: {
          icon: [
            { url: '/favicons/meac/favicon.ico' },
            { url: '/favicons/meac/favicon-16x16.png', sizes: '16x16' },
            { url: '/favicons/meac/favicon-32x32.png', sizes: '32x32' },
            { url: '/favicons/meac/favicon-128x128.png', sizes: '128x128' },
          ],
          apple: [
            {
              url: '/favicons/meac/apple-touch-icon-180x180.png',
              sizes: '180x180',
              type: 'image/png',
            },
          ],
        },
      },
      homepageContent: {
        allSpecialties: {
          enabled: true,
          content: [
            {
              name: 'specialties.allergyImmunology',
              slug: 'allergy-and-immunology',
            },
            {
              name: 'specialties.oncology',
              href: 'https://cancer.montefioreeinstein.org/doctors/profiles',
            },
            {
              name: 'specialties.cardiothoracicSurgery',
              slug: 'cardiothoracic-surgery',
            },
            {
              name: 'specialties.dentistry',
              slug: 'dentistry',
            },
            {
              name: 'specialties.dermatology',
              slug: 'dermatology',
            },
            {
              name: 'specialties.endocrinology',
              slug: 'endocrinology,-diabetes,-metabolism',
            },
            {
              name: 'specialties.familyPractice',
              slug: 'family-practice',
            },
            {
              name: 'specialties.gastroenterology',
              slug: 'gastroenterology',
            },
            {
              name: 'specialties.hematology',
              slug: 'hematology',
            },
            {
              name: 'specialties.hepatology',
              slug: 'hepatology',
            },
            {
              name: 'specialties.palliative',
              slug: 'hospice-and-palliative-medicine',
            },
            {
              name: 'specialties.infectiousDisease',
              slug: 'infectious-disease',
            },
            {
              name: 'specialties.nephrology',
              slug: 'nephrology',
            },
            {
              name: 'specialties.neurologicalSurgery',
              slug: 'neurological-surgery',
            },
            {
              name: 'specialties.neurology',
              slug: 'neurology',
            },
            {
              name: 'specialties.obstetricsGynecology',
              slug: 'obstetrics-and-gynecology',
            },
            {
              name: 'specialties.ophthalmology',
              slug: 'ophthalmology',
            },
            {
              name: 'specialties.orthopedicSurgery',
              slug: 'orthopedic-surgery',
            },
            {
              name: 'specialties.ent',
              slug: 'otolaryngology',
            },
            {
              name: 'specialties.pediatrics',
              slug: 'pediatrics',
            },
            {
              name: 'specialties.transplantSurgery',
              slug: 'transplant-surgery',
            },
          ],
        },
        cta: {
          enabled: false,
        },
        frequentlySearchedSpecialties: {
          enabled: true,
          content: [
            {
              icon: InsuranceHandsMono,
              title: 'frequently_searched_specialties.primary_care_card.title',
              body: 'frequently_searched_specialties.primary_care_card.body',
              slug: 'primary-care',
            },
            {
              icon: RadiologyScanMri,
              title: 'frequently_searched_specialties.imaging_card.title',
              body: 'frequently_searched_specialties.imaging_card.body',
              slug: 'radiology',
            },
          ],
        },
      },
      decisionTree: {
        mainPhoneNumber: '800-MD-MONTE',
        scenesConfig: {
          'endocrinology-weight-management-check-error':
            WEIGHT_MANAGEMENT_ERROR_CONTACTS,
        },
        specialtyMap: {
          // Cardiology
          'advanced heart failure and transplant cardiology': 'cardiology',
          arrhythmia: 'cardiology',
          'cardiac electrophysiology': 'cardiology',
          'cardio-genetics': 'cardiology',
          'cardio-obstetrics': 'cardiology',
          'cardio-oncology': 'cardiology',
          'cardiothoracic surgery': 'cardiology',
          'cardiovascular disease': 'cardiology',
          'general cardiology': 'cardiology',
          'hypertrophic cardiomyopathy': 'cardiology',
          'interventional cardiology': 'cardiology',
          'preventive cardiology': 'cardiology',
          'structural-valvular heart disease': 'cardiology',
          // Dermatology
          dermatology: 'dermatology',
          'general dermatology': 'dermatology',
          'cosmetic dermatology': 'dermatology',
          'pediatric dermatology': 'dermatology',
          'dermatologic surgery': 'dermatology',
          'mohs surgery': 'dermatology',
          // Allergy
          'food allergies': 'allergy',
          'drug allergies': 'allergy',
          'general allergy and immunology': 'allergy',
          'allergy and immunology': 'allergy',
          // Endocrinology
          'pediatric endocrinology and diabetes': 'endocrinology',
          'endocrinology, diabetes and metabolism': 'endocrinology',
          'reproductive endocrinology and infertility': 'endocrinology',
          'type 1 diabetes': 'endocrinology',
          'type 2 diabetes': 'endocrinology',
          'thyroid cancer': 'endocrinology',
          'thyroid disorders': 'endocrinology',
          'osteoporosis and metabolic bone disease': 'endocrinology',
          'weight management': 'endocrinology',
          'pituitary and adrenal disorders': 'endocrinology',
          // Gastroenterology
          gastroenterology: 'gastroenterology',
          'pediatric gastroenterology': 'gastroenterology',
          // Orthopedics
          'orthopedic surgery': 'orthopedics',
          'hand surgery': 'orthopedics',
          'wrist surgery': 'orthopedics',
          'foot and ankle surgery': 'orthopedics',
          'knee surgery': 'orthopedics',
          'hip surgery': 'orthopedics',
          'shoulder surgery': 'orthopedics',
          'spine surgery': 'orthopedics',
          'orthopedic trauma': 'orthopedics',
          'orthopedic oncology': 'orthopedics',
          'joint replacement': 'orthopedics',
          'sports medicine': 'orthopedics',
          podiatry: 'orthopedics',
          'upper extremity surgery': 'orthopedics',
          scoliosis: 'orthopedics',
          pediatrics: 'pediatrics',
        },
      },
      iconIsoRedirect: '/favicons/meac/favicon.ico',
      appleTouchIconPath: '/favicons/meac/apple-touch-icon-180x180.png',
      appointmentTypes: [
        {
          key: 'in-person',
          visitTypeId: '1437',
          label: 'visitType.newPatientConsult.label',
          description: 'visitType.newPatientConsult.description',
          modality: 'in-person',
        },
        {
          key: 'annual-physical',
          visitTypeId: '1201',
          label: 'visitType.annualPhysical.label',
          description: 'visitType.annualPhysical.description',
          modality: 'in-person',
        },
      ],
      defaultMaxDistance: 5,
      legal: {
        smsTermsUrl:
          'https://mychart.montefiore.org/mychart/Authentication/Login?mode=stdfile&option=termsandconditions',
        privacyPolicyUrl:
          'https://montefioreeinstein.org/patient-care/privacy-policy/web-mobile',
      },
      findDoctorUrl: {
        [Environment.PROD]:
          'https://montefioreeinsteinadvancedcare.org/providers',
        [Environment.STAGING]:
          'https://content.montefioreeinsteinadvancedcare.org/providers',
        [Environment.QA]: undefined,
        [Environment.DEV]: undefined,
        [Environment.LOCAL]: undefined,
      }[deployEnvironment],
      hasOwnFindDoctorPage: true,
      providerRedirects: MONTEFIORE_PROVIDER_REDIRECTS,
      translation: {
        enabledFields: [
          'biography',
          'gender',
          'languages',
          'degrees',
          'specialties',
          'clinicalFocus',
          'researchFocus',
          'fellowships',
          'medicalEducation',
          'residencies',
          'internship',
          'conditions',
          'boardCertifications',
          'locationNames',
        ],
        glossaryId: 'montefiore-glossary',
      },
      sorting: {
        options: [
          {
            type: 'item',
            label: 'searchFilters.availability',
            value: 'availability',
            query: '-scheduling,-availability,-networks',
            queryWithDistance: '-scheduling,-relevance,-networks',
          },
          {
            type: 'item',
            label: 'searchFilters.distance',
            value: 'distance',
            query: 'distance',
          },
          {
            type: 'section',
            section: 'searchFilters.alphabetical',
            label: 'A – Z',
            value: 'name',
            query: 'name',
          },
          {
            type: 'section',
            section: 'searchFilters.alphabetical',
            label: 'Z – A',
            value: '-name',
            query: '-name',
          },
        ],
        default: 'availability',
      },
      sdk: {
        directoryUrl: {
          [Environment.PROD]:
            'https://doctors.montefioreeinsteinadvancedcare.org',
          [Environment.STAGING]: 'https://meac.pds.stage.fabrichealth.com',
          [Environment.QA]: 'https://meac.pds.qa.fabrichealth.com',
          [Environment.DEV]: 'https://meac.pds.dev.fabrichealth.com',
          [Environment.LOCAL]: 'http://localhost:3000',
        }[deployEnvironment],
        theme: {
          providerSearchForm: 'bg-white py-4 pr-6',
        },
      },
    },
    [ContextKeySchema.enum.acme]: {
      name: 'Acme Health',
      branding: {
        logoUrl: '/acme-logo.svg',
        cssVariables: {
          headerColor: '#005CB7',
        },
        fonts: {
          sans: { key: 'inter', source: 'GOOGLE' },
          serif: 'Libre Caslon Text, serif',
        },
      },
      homepageContent: {
        allSpecialties: {
          enabled: true,
          content: [
            {
              name: 'specialties.allergyImmunology',
              slug: 'allergy-and-immunology',
            },
            {
              name: 'specialties.hematologyOncology',
              slug: 'oncology',
            },
            {
              name: 'specialties.cardiothoracicSurgery',
              slug: 'cardiothoracic-surgery',
            },
            {
              name: 'specialties.dentistry',
              slug: 'dentistry',
            },
            {
              name: 'specialties.dermatology',
              slug: 'dermatology',
            },
            {
              name: 'specialties.endocrinology',
              slug: 'endocrinology',
            },
          ],
        },
        cta: {
          enabled: false,
        },
        frequentlySearchedSpecialties: {
          enabled: true,
          content: [
            {
              icon: InsuranceHandsMono,
              title: 'frequently_searched_specialties.primary_care_card.title',
              body: 'frequently_searched_specialties.primary_care_card.body',
              slug: 'primary-care',
            },
            {
              icon: RadiologyScanMri,
              title: 'frequently_searched_specialties.imaging_card.title',
              body: 'frequently_searched_specialties.imaging_card.body',
              slug: 'radiology',
            },
          ],
        },
      },
      decisionTree: {
        mainPhoneNumber: '800-555-5555',
        scenesConfig: {},
        specialtyMap: {},
      },
      // MyChart is not available for acme — empty URLs are intentional
      analytics: {
        gtmId: undefined,
      },
      sitemap: {},
      metadata: {
        title: 'Provider directory | Acme Health',
        description: 'View a list of providers at Acme Health',
        keywords: '',
        openGraph: {
          title: 'Provider directory',
          description: 'View a list of providers at Acme Health.',
        },
      },
      appointmentTypes: [
        {
          key: 'in-person',
          visitTypeId: '1437',
          label: 'visitType.newPatientConsult.label',
          description: 'visitType.newPatientConsult.description',
          modality: 'in-person',
        },
        {
          key: 'annual-physical',
          visitTypeId: '1201',
          label: 'visitType.annualPhysical.label',
          description: 'visitType.annualPhysical.description',
          modality: 'in-person',
        },
        {
          key: 'video',
          visitTypeId: '1500',
          label: 'visitType.generalTelehealth.label',
          description: 'visitType.generalTelehealth.description',
          modality: 'video',
        },
      ],
      defaultMaxDistance: 10,
      sorting: {
        options: [
          {
            type: 'item',
            label: 'searchFilters.availability',
            value: 'availability',
            query: '-scheduling,-availability,-networks',
            queryWithDistance: '-scheduling,-relevance,-networks',
          },
          {
            type: 'item',
            label: 'searchFilters.distance',
            value: 'distance',
            query: 'distance',
          },
          {
            type: 'section',
            section: 'searchFilters.alphabetical',
            label: 'A – Z',
            value: 'name',
            query: 'name',
          },
          {
            type: 'section',
            section: 'searchFilters.alphabetical',
            label: 'Z – A',
            value: '-name',
            query: '-name',
          },
        ],
        default: 'availability',
      },
    },
  };
};

// featureFlags is unused now but kept for the next flag-gated config override.
export const getAllOrgConfigs = (
  _featureFlags: FeatureFlags = {},
): AllClientConfigs => {
  _clientOrgConfigs ??= buildClientOrgConfigs();
  const configs: (AllClientConfigs | DeepPartial<AllClientConfigs>)[] = [
    _clientOrgConfigs,
  ];

  return mergeConfigs(configs) as AllClientConfigs;
};

export const getClientOrgConfig = (
  key: ClientOrgContextKey,
  featureFlags: FeatureFlags = {},
): LocalDirectoryConfig => {
  const allClientOrgConfigs = getAllOrgConfigs(featureFlags);

  const config = allClientOrgConfigs[key];

  if (!config) {
    throw new Error(
      `Directory "${key}" was resolved from /v1/config but has no entry in CLIENT_ORG_CONFIGS. ` +
        `Add a local entry for remaining unmigrated fields (see DIR-3545 onboarding), then re-deploy.`,
    );
  }

  return config;
};

export type FilterFormGroupByProvider = Record<
  ClientOrgContextKey,
  FilterFormGroup[]
>;

export const getAllFilterFormGroups = (
  featureFlags: FeatureFlags = {},
  directoryDisplayNames?: Partial<Record<ClientOrgContextKey, string>>,
  /**
   * When false, runtime label lookup is silent (no warn / warn-cache). Used while
   * eagerly building every directory's form groups for a request whose active
   * directory does not own the Montefiore hospitalAssociations labels (e.g. acme).
   */
  resolveRuntimeDisplayNames = true,
): FilterFormGroupByProvider => {
  const allOrgConfigs = getAllOrgConfigs();
  const getDisplayName = (key: ClientOrgContextKey): string => {
    if (!featureFlags.isConfigRolloutGroupEnabled) {
      return allOrgConfigs[key].name;
    }
    if (!resolveRuntimeDisplayNames) {
      return directoryDisplayNames?.[key] ?? '';
    }
    return resolveDirectoryDisplayName(key, directoryDisplayNames);
  };

  return {
    [ContextKeySchema.enum.montefiore_mmc]: [
      {
        component: 'group',
        name: 'search',
        label: 'searchFilters.searchBy',
        fields: [
          {
            component: 'select',
            name: 'specialties',
            label: 'searchFilters.specialty',
            placeholder: 'searchFilters.specialty',
            required: true,
            multiple: false,
            options: [],
          },
          {
            component: 'select',
            name: 'providerNames',
            label: 'searchFilters.doctorName',
            placeholder: 'searchFilters.doctorName',
            required: false,
            multiple: false,
            options: [],
          },
          {
            component: 'select',
            name: 'geolocation',
            label: 'searchFilters.location',
            placeholder: 'searchFilters.location',
            required: false,
            multiple: false,
            options: [],
          },
          {
            component: 'select',
            name: 'maxDistance',
            label: 'searchFilters.distance',
            placeholder: 'searchFilters.distance',
            required: true,
            multiple: false,
            defaultKey:
              allOrgConfigs.montefiore_mmc.defaultMaxDistance.toString(),
            options: [
              {
                label: 'searchFilters.withinDistance',
                key: '1',
                slug: '1',
                isDisabled: false,
              },
              {
                label: 'searchFilters.withinDistance',
                key: '5',
                slug: '5',
                isDisabled: false,
              },
              {
                label: 'searchFilters.withinDistance',
                key: '10',
                slug: '10',
                isDisabled: false,
              },
              {
                label: 'searchFilters.withinDistance',
                key: '25',
                slug: '25',
                isDisabled: false,
              },
              {
                label: 'searchFilters.withinDistance',
                key: '50',
                slug: '50',
                isDisabled: false,
              },
            ],
          },
        ],
      },
      {
        component: 'group',
        name: 'check',
        label: null,
        fields: [
          /* HIDE_ACCEPTING_PATIENTS, see DIR-554
          {
          component: 'checkbox',
          name: 'acceptingNewPatients',
          label: 'searchFilters.newPatients',
          options: [],
        }, */
          {
            component: 'checkbox',
            name: 'canOnlineSchedule',
            label: 'searchFilters.onlineBooking',
            options: [],
          },
          {
            component: 'checkbox',
            name: 'isPcp',
            label: 'searchFilters.pcp',
            options: [],
          },
        ],
      },
      {
        component: 'group',
        name: 'search',
        label: 'searchFilters.filterBy',
        fields: [
          {
            component: 'select',
            name: 'gender',
            label: 'searchFilters.gender',
            placeholder: 'searchFilters.gender',
            required: false,
            multiple: false,
            options: [],
          },
          {
            component: 'select',
            name: 'hospitalAssociations',
            label: 'searchFilters.hospitalAssociations',
            placeholder: 'searchFilters.hospitalAssociations',
            required: false,
            multiple: false,
            options: [
              {
                key: 'mmc',
                label: getDisplayName(ContextKeySchema.enum.montefiore_mmc),
                slug: 'mmc',
                isDisabled: false,
              },
              {
                key: 'meac',
                label: getDisplayName(ContextKeySchema.enum.montefiore_meac),
                slug: 'meac',
                isDisabled: false,
              },
              {
                key: 'wph',
                label: getDisplayName(ContextKeySchema.enum.montefiore_wph),
                slug: 'wph',
                isDisabled: false,
              },
              {
                key: 'mmv',
                label: 'Montefiore Mount Vernon',
                slug: 'mmv',
                isDisabled: false,
              },
              {
                key: 'mnr',
                label: 'Montefiore New Rochelle',
                slug: 'mnr',
                isDisabled: false,
              },
            ],
          },
          {
            component: 'select',
            name: 'languages',
            label: 'searchFilters.language',
            placeholder: 'searchFilters.language',
            required: false,
            options: [],
            multiple: false,
          },
          {
            component: 'select',
            name: 'ageGroups',
            label: 'searchFilters.ageGroup',
            placeholder: 'searchFilters.ageGroup',
            required: false,
            multiple: false,
            options: [],
          },

          {
            component: 'select',
            name: 'timeOfDay',
            label: 'searchFilters.timeOfDay',
            placeholder: 'searchFilters.timeOfDay',
            required: false,
            multiple: true,
            featureFlag: 'isTimeDayAvailabilityFilterEnabled',
            options: [
              {
                key: 'morning',
                slug: 'morning',
                label: 'searchFilters.timeOfDayMorning',
                isDisabled: false,
              },
              {
                key: 'afternoon',
                slug: 'afternoon',
                label: 'searchFilters.timeOfDayAfternoon',
                isDisabled: false,
              },
              {
                key: 'evening',
                slug: 'evening',
                label: 'searchFilters.timeOfDayEvening',
                isDisabled: false,
              },
            ],
          },
          {
            component: 'select',
            name: 'daysOfWeek',
            label: 'searchFilters.daysOfWeek',
            placeholder: 'searchFilters.daysOfWeek',
            required: false,
            multiple: true,
            featureFlag: 'isTimeDayAvailabilityFilterEnabled',
            options: [
              {
                key: 'mon',
                slug: 'mon',
                label: 'searchFilters.dayMon',
                isDisabled: false,
              },
              {
                key: 'tue',
                slug: 'tue',
                label: 'searchFilters.dayTue',
                isDisabled: false,
              },
              {
                key: 'wed',
                slug: 'wed',
                label: 'searchFilters.dayWed',
                isDisabled: false,
              },
              {
                key: 'thu',
                slug: 'thu',
                label: 'searchFilters.dayThu',
                isDisabled: false,
              },
              {
                key: 'fri',
                slug: 'fri',
                label: 'searchFilters.dayFri',
                isDisabled: false,
              },
              {
                key: 'sat',
                slug: 'sat',
                label: 'searchFilters.daySat',
                isDisabled: false,
              },
              {
                key: 'sun',
                slug: 'sun',
                label: 'searchFilters.daySun',
                isDisabled: false,
              },
            ],
          },
        ],
      },
    ],
    [ContextKeySchema.enum.montefiore_wph]: [
      {
        component: 'group',
        name: 'search',
        label: 'searchFilters.searchBy',
        fields: [
          {
            component: 'select',
            name: 'specialties',
            label: 'searchFilters.specialty',
            placeholder: 'searchFilters.specialty',
            required: true,
            multiple: false,
            options: [],
          },
          {
            component: 'select',
            name: 'providerNames',
            label: 'searchFilters.doctorName',
            placeholder: 'searchFilters.doctorName',
            required: false,
            multiple: false,
            options: [],
          },
          {
            component: 'select',
            name: 'geolocation',
            label: 'searchFilters.location',
            placeholder: 'searchFilters.location',
            required: false,
            multiple: false,
            options: [],
          },
          {
            component: 'select',
            name: 'maxDistance',
            label: 'searchFilters.distance',
            placeholder: 'searchFilters.distance',
            required: true,
            multiple: false,
            defaultKey:
              allOrgConfigs.montefiore_wph.defaultMaxDistance.toString(),
            options: [
              {
                label: 'searchFilters.withinDistance',
                key: '1',
                slug: '1',
                isDisabled: false,
              },
              {
                label: 'searchFilters.withinDistance',
                key: '5',
                slug: '5',
                isDisabled: false,
              },
              {
                label: 'searchFilters.withinDistance',
                key: '10',
                slug: '10',
                isDisabled: false,
              },
              {
                label: 'searchFilters.withinDistance',
                key: '25',
                slug: '25',
                isDisabled: false,
              },
              {
                label: 'searchFilters.withinDistance',
                key: '50',
                slug: '50',
                isDisabled: false,
              },
            ],
          },
        ],
      },
      {
        component: 'group',
        name: 'check',
        label: null,
        fields: [
          /* HIDE_ACCEPTING_PATIENTS, see DIR-554
          {
          component: 'checkbox',
          name: 'acceptingNewPatients',
          label: 'searchFilters.newPatients',
          options: [],
        }, */
          {
            component: 'checkbox',
            name: 'canOnlineSchedule',
            label: 'searchFilters.onlineBooking',
            options: [],
          },
          {
            component: 'checkbox',
            name: 'isPcp',
            label: 'searchFilters.pcp',
            options: [],
          },
        ],
      },
      {
        component: 'group',
        name: 'search',
        label: 'searchFilters.filterBy',
        fields: [
          {
            component: 'select',
            name: 'gender',
            label: 'searchFilters.gender',
            placeholder: 'searchFilters.gender',
            required: false,
            multiple: false,
            options: [],
          },
          {
            component: 'select',
            name: 'languages',
            label: 'searchFilters.language',
            placeholder: 'searchFilters.language',
            required: false,
            options: [],
            multiple: false,
          },
          {
            component: 'select',
            name: 'ageGroups',
            label: 'searchFilters.ageGroup',
            placeholder: 'searchFilters.ageGroup',
            required: false,
            multiple: false,
            options: [],
          },

          {
            component: 'select',
            name: 'timeOfDay',
            label: 'searchFilters.timeOfDay',
            placeholder: 'searchFilters.timeOfDay',
            required: false,
            multiple: true,
            featureFlag: 'isTimeDayAvailabilityFilterEnabled',
            options: [
              {
                key: 'morning',
                slug: 'morning',
                label: 'searchFilters.timeOfDayMorning',
                isDisabled: false,
              },
              {
                key: 'afternoon',
                slug: 'afternoon',
                label: 'searchFilters.timeOfDayAfternoon',
                isDisabled: false,
              },
              {
                key: 'evening',
                slug: 'evening',
                label: 'searchFilters.timeOfDayEvening',
                isDisabled: false,
              },
            ],
          },
          {
            component: 'select',
            name: 'daysOfWeek',
            label: 'searchFilters.daysOfWeek',
            placeholder: 'searchFilters.daysOfWeek',
            required: false,
            multiple: true,
            featureFlag: 'isTimeDayAvailabilityFilterEnabled',
            options: [
              {
                key: 'mon',
                slug: 'mon',
                label: 'searchFilters.dayMon',
                isDisabled: false,
              },
              {
                key: 'tue',
                slug: 'tue',
                label: 'searchFilters.dayTue',
                isDisabled: false,
              },
              {
                key: 'wed',
                slug: 'wed',
                label: 'searchFilters.dayWed',
                isDisabled: false,
              },
              {
                key: 'thu',
                slug: 'thu',
                label: 'searchFilters.dayThu',
                isDisabled: false,
              },
              {
                key: 'fri',
                slug: 'fri',
                label: 'searchFilters.dayFri',
                isDisabled: false,
              },
              {
                key: 'sat',
                slug: 'sat',
                label: 'searchFilters.daySat',
                isDisabled: false,
              },
              {
                key: 'sun',
                slug: 'sun',
                label: 'searchFilters.daySun',
                isDisabled: false,
              },
            ],
          },
        ],
      },
    ],
    [ContextKeySchema.enum.acme]: [],
    [ContextKeySchema.enum.montefiore_meac]: [
      {
        component: 'group',
        name: 'search',
        label: 'searchFilters.searchBy',
        fields: [
          {
            component: 'select',
            name: 'specialties',
            label: 'searchFilters.specialty',
            placeholder: 'searchFilters.specialty',
            required: true,
            multiple: false,
            options: [],
          },
          {
            component: 'select',
            name: 'providerNames',
            label: 'searchFilters.doctorName',
            placeholder: 'searchFilters.doctorName',
            required: false,
            multiple: false,
            options: [],
          },
          {
            component: 'select',
            name: 'geolocation',
            label: 'searchFilters.location',
            placeholder: 'searchFilters.location',
            required: false,
            multiple: false,
            options: [],
          },
          {
            component: 'select',
            name: 'maxDistance',
            label: 'searchFilters.distance',
            placeholder: 'searchFilters.distance',
            required: true,
            multiple: false,
            defaultKey:
              allOrgConfigs.montefiore_meac.defaultMaxDistance.toString(),
            options: [
              {
                label: 'searchFilters.withinDistance',
                key: '1',
                slug: '1',
                isDisabled: false,
              },
              {
                label: 'searchFilters.withinDistance',
                key: '5',
                slug: '5',
                isDisabled: false,
              },
              {
                label: 'searchFilters.withinDistance',
                key: '10',
                slug: '10',
                isDisabled: false,
              },
              {
                label: 'searchFilters.withinDistance',
                key: '25',
                slug: '25',
                isDisabled: false,
              },
              {
                label: 'searchFilters.withinDistance',
                key: '50',
                slug: '50',
                isDisabled: false,
              },
            ],
          },
        ],
      },
      {
        component: 'group',
        name: 'check',
        label: null,
        fields: [
          {
            component: 'checkbox',
            name: 'canOnlineSchedule',
            label: 'searchFilters.onlineBooking',
            options: [],
          },
          {
            component: 'checkbox',
            name: 'isPcp',
            label: 'searchFilters.pcp',
            options: [],
          },
        ],
      },
      {
        component: 'group',
        name: 'search',
        label: 'searchFilters.filterBy',
        fields: [
          {
            component: 'select',
            name: 'gender',
            label: 'searchFilters.gender',
            placeholder: 'searchFilters.gender',
            required: false,
            multiple: false,
            options: [],
          },
          {
            component: 'select',
            name: 'languages',
            label: 'searchFilters.language',
            placeholder: 'searchFilters.language',
            required: false,
            options: [],
            multiple: false,
          },
          {
            component: 'select',
            name: 'ageGroups',
            label: 'searchFilters.ageGroup',
            placeholder: 'searchFilters.ageGroup',
            required: false,
            multiple: false,
            options: [],
          },

          {
            component: 'select',
            name: 'timeOfDay',
            label: 'searchFilters.timeOfDay',
            placeholder: 'searchFilters.timeOfDay',
            required: false,
            multiple: true,
            featureFlag: 'isTimeDayAvailabilityFilterEnabled',
            options: [
              {
                key: 'morning',
                slug: 'morning',
                label: 'searchFilters.timeOfDayMorning',
                isDisabled: false,
              },
              {
                key: 'afternoon',
                slug: 'afternoon',
                label: 'searchFilters.timeOfDayAfternoon',
                isDisabled: false,
              },
              {
                key: 'evening',
                slug: 'evening',
                label: 'searchFilters.timeOfDayEvening',
                isDisabled: false,
              },
            ],
          },
          {
            component: 'select',
            name: 'daysOfWeek',
            label: 'searchFilters.daysOfWeek',
            placeholder: 'searchFilters.daysOfWeek',
            required: false,
            multiple: true,
            featureFlag: 'isTimeDayAvailabilityFilterEnabled',
            options: [
              {
                key: 'mon',
                slug: 'mon',
                label: 'searchFilters.dayMon',
                isDisabled: false,
              },
              {
                key: 'tue',
                slug: 'tue',
                label: 'searchFilters.dayTue',
                isDisabled: false,
              },
              {
                key: 'wed',
                slug: 'wed',
                label: 'searchFilters.dayWed',
                isDisabled: false,
              },
              {
                key: 'thu',
                slug: 'thu',
                label: 'searchFilters.dayThu',
                isDisabled: false,
              },
              {
                key: 'fri',
                slug: 'fri',
                label: 'searchFilters.dayFri',
                isDisabled: false,
              },
              {
                key: 'sat',
                slug: 'sat',
                label: 'searchFilters.daySat',
                isDisabled: false,
              },
              {
                key: 'sun',
                slug: 'sun',
                label: 'searchFilters.daySun',
                isDisabled: false,
              },
            ],
          },
        ],
      },
    ],
  };
};

export const getFilterFormGroups = (
  key: ClientOrgContextKey,
  featureFlags: FeatureFlags = {},
  directoryDisplayNames?: Partial<Record<ClientOrgContextKey, string>>,
): FilterFormGroup[] => {
  // Only the MMC form group resolves sibling labels via directoryDisplayNames.
  // Other directories still build the full map eagerly — skip warn/cache there.
  const resolveRuntimeDisplayNames =
    key === ContextKeySchema.enum.montefiore_mmc;
  const allFilterFormGroups = getAllFilterFormGroups(
    featureFlags,
    directoryDisplayNames,
    resolveRuntimeDisplayNames,
  );

  const formGroups = allFilterFormGroups[key];

  if (!formGroups) {
    throw new Error(`No filter form groups found for key: ${key}`);
  }

  return formGroups;
};
